import { CATEGORIES, PRODUCTS } from '../mock/mockData';
import { categoryApi, productApi } from './api';

const slugify = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const numberValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const calculateDiscount = (marketPrice, price) => {
  if (!marketPrice || !price || marketPrice <= price) return 0;
  return Math.max(0, Math.round(((marketPrice - price) / marketPrice) * 100));
};

const firstImageUrl = (product) => {
  const first = product.images?.find?.((image) => image?.isThumbnail) || product.images?.[0];
  if (typeof first === 'string') return first;
  return first?.url || product.image || '';
};

const normalizeImageUrl = (image) => (typeof image === 'string' ? image : image?.url || '');

export const normalizeProduct = (product) => {
  if (!product) return product;

  const price = numberValue(product.price, product.sellingPrice, product.salePrice, product.selling_price);
  const marketPrice = numberValue(product.marketPrice, product.mrp, product.MRP, product.listPrice, product.price);
  const categoryValue = typeof product.category === 'object'
    ? product.category.slug || product.category.name || product.category._id || product.category.id
    : product.category;

  const images = Array.isArray(product.images)
    ? product.images.map((image) => (typeof image === 'string' ? image : image?.url)).filter(Boolean)
    : [];
  const videos = Array.isArray(product.videos)
    ? product.videos.map((video) => (typeof video === 'string' ? { url: video } : video)).filter((video) => video?.url)
    : [];
  const variants = Array.isArray(product.variants)
    ? product.variants.map((variant) => ({
        ...variant,
        id: variant.id || variant._id || variant.sku,
        colorName: variant.colorName || variant.color || '',
        colorImage: normalizeImageUrl(variant.colorImage),
        images: Array.isArray(variant.images) ? variant.images.map((image) => (typeof image === 'string' ? image : image?.url)).filter(Boolean) : [],
        videos: Array.isArray(variant.videos) ? variant.videos.map((video) => (typeof video === 'string' ? { url: video } : video)).filter((video) => video?.url) : []
      }))
    : [];

  return {
    ...product,
    id: product.id || product._id || product.slug,
    image: firstImageUrl(product),
    images: images.length ? images : [firstImageUrl(product)].filter(Boolean),
    videos,
    variants,
    category: slugify(categoryValue) || categoryValue,
    categoryName: typeof product.category === 'object' ? product.category.name : product.categoryName,
    colorImage: normalizeImageUrl(product.colorImage),
    price,
    rating: product.rating ?? product.ratingsAverage ?? 0,
    reviews: product.reviews ?? product.totalReviews ?? 0,
    marketPrice: marketPrice || price,
    discount: calculateDiscount(marketPrice, price)
  };
};

const productWithVariant = (product, variant, index) => {
  const variantId = variant.id || variant._id || variant.sku || index;
  const variantName = variant.name || [variant.colorName || variant.color, variant.size].filter(Boolean).join(' / ');
  const price = numberValue(variant.price, product.price);
  const marketPrice = numberValue(variant.marketPrice, product.marketPrice, price);
  const image = variant.images?.[0] || variant.colorImage || product.image;

  return {
    ...product,
    ...variant,
    id: `${product.id}__variant__${variantId}`,
    productId: product.id,
    variantId,
    name: variantName ? `${product.name} - ${variantName}` : product.name,
    image,
    images: variant.images?.length ? variant.images : product.images,
    price,
    marketPrice,
    discount: calculateDiscount(marketPrice, price),
    stock: variant.stock ?? product.stock,
    variantLabel: variantName,
    isVariantCard: true,
    parentProduct: product
  };
};

export const normalizeProducts = (products) =>
  products.flatMap((rawProduct) => {
    const product = normalizeProduct(rawProduct);
    if (!product?.id) return [];
    const variantCards = (product.variants || []).map((variant, index) => productWithVariant(product, variant, index));
    return [product, ...variantCards];
  });

export const normalizeCategory = (category) => {
  if (!category) return null;
  const name = category.name || category.label || category.title || category.category || '';
  const id = slugify(category.slug || name || category.id || category._id);
  if (!id || !name) return null;

  return {
    ...category,
    id,
    name,
    image: typeof category.image === 'string' ? category.image : category.image?.url || ''
  };
};

export const normalizeCategories = (categories) =>
  categories.map(normalizeCategory).filter(Boolean);

export const categoriesFromProducts = (products) => {
  const map = new Map();
  products.forEach((product) => {
    const id = slugify(product.category);
    if (!id || map.has(id)) return;
    map.set(id, {
      id,
      name: product.categoryName || String(product.category || '').replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
      image: product.image || ''
    });
  });
  return Array.from(map.values());
};

const hasProducts = (products) => Array.isArray(products) && products.length > 0;

export const getCatalogProducts = async (params = {}) => {
  try {
    const response = await productApi.list(params);
    const products = normalizeProducts(response.data?.data || response.data || []);
    if (hasProducts(products)) return { products, source: 'backend' };
  } catch (error) {
    console.warn('Using mock products because backend products are unavailable.', error?.message);
  }

  return { products: PRODUCTS, source: 'mock' };
};

export const getCatalogCategories = async (products = []) => {
  try {
    const response = await categoryApi.list({ limit: 200 });
    const categories = normalizeCategories(response.data?.data || response.data || [])
      .filter((category) => category.isActive !== false);
    if (categories.length) return { categories, source: 'backend' };
  } catch (error) {
    console.warn('Using product-derived categories because backend categories are unavailable.', error?.message);
  }

  const derivedCategories = categoriesFromProducts(products);
  if (derivedCategories.length) return { categories: derivedCategories, source: 'products' };

  try {
    const response = await productApi.list({ limit: 100 });
    const backendProducts = normalizeProducts(response.data?.data || response.data || []);
    const backendProductCategories = categoriesFromProducts(backendProducts);
    if (backendProductCategories.length) return { categories: backendProductCategories, source: 'products' };
  } catch (error) {
    console.warn('Using mock categories because backend product categories are unavailable.', error?.message);
  }

  return { categories: CATEGORIES, source: 'mock' };
};

export const getCatalogProduct = async (id) => {
  try {
    const response = await productApi.get(id);
    const product = normalizeProduct(response.data?.data || response.data);
    if (product?.id) return { product, source: 'backend' };
  } catch (error) {
    console.warn('Using mock product because backend product is unavailable.', error?.message);
  }

  return {
    product: PRODUCTS.find((product) => product.id === id || product.slug === id),
    source: 'mock'
  };
};
